export const isEmpty = (str) => {
  return (!str || str.length === 0);
}

export const isEmptyObj = (obj) => {
  return Object.keys(obj).length === 0;
}

export const decodedJWTPayload = (token) => {
  if (token) {
    const array = token.split('.');
    const decodedString = atob(array[1]);
    const obj = JSON.parse(decodedString);
    return obj;
  }
  return undefined;
}

export const isEmailValid = (value) => {
  if (isEmpty(value)) {
    return true;
  }
  if (value && value.match(/^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/)) {
    return true;
  }

  return false;
}

export const validatePassword = (password) => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const conditionCount = [hasUpperCase, hasLowerCase, hasNumber].filter(Boolean).length;

  return password.length >= 8 && conditionCount >= 3;
};

/**
 * This function checks if there are any errors in the generated error response:
 
It recursively checks all values in the error response.
If any field has an error message (i.e., it is not undefined), it indicates an error.
If all values are undefined, the data is error-free, and it returns true.
 * @param {*} errorResponse 
 * @returns 
 */
export function hasNoErrors(errorResponse) {
  // Check if any field has an error message (i.e., not undefined)
  function checkForErrors(obj) {
    return Object.values(obj).every(value => {
      if (Array.isArray(value)) {
        return value.every(item => checkForErrors(item)); // Recurse for each item in array
      } else if (typeof value === 'object' && value !== null) {
        return checkForErrors(value); // Recurse for each object
      } else {
        return value === undefined; // No error message means no error
      }
    });
  }

  return checkForErrors(errorResponse);
}



/**
 * Recursively copies a JSON object or array, setting all values to empty strings.
 * 
 * This function takes a JSON object or array and creates a new object or array with the same structure, 
 * but with all values replaced by empty strings. It handles nested objects and arrays as well.
 * 
 * @param {Object|Array} json - The JSON object or array to copy with empty values.
 * @returns {Object|Array} A new object or array with the same structure as the input, but with all values as empty strings.
 */
export function copyJsonWithEmptyValues(json) {

  // Check if the input is neither an object nor an array, return an empty string
  if (typeof json !== 'object' || json === null) {
    return '';
  }

  // If the input is an array, recursively apply the function to each item in the array
  if (Array.isArray(json)) {
    return json.map(item => copyJsonWithEmptyValues(item));
  }

  // Create a new object and recursively copy each key-value pair with empty values
  const newObj = {};
  for (const key in json) {
    if (json.hasOwnProperty(key)) {
      newObj[key] = copyJsonWithEmptyValues(json[key]);
    }
  }

  // Return the newly created object
  return newObj;

}

/**
 * Converts empty strings to null in a given object.
 * 
 * @param {Object} obj - The object to process.
 * @return {Object} - The updated object with empty strings converted to null.
 */
export function emptyStringToNull(obj) {
  // Iterate over the object's keys
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      // Check if the value is an empty string
      if (obj[key] === "") {
        obj[key] = null;
      }
    }
  }
  return obj;
}

/**
 * Compares two JSON objects (json1 and json2) of the same structure and
 *  returns a new JSON object containing the fields where the values differ.
 *  It performs a deep comparison, recursively checking nested objects, and tracks the values in both JSON objects. 
 * If the value of a field is different between json1 and json2, 
 * the method includes that field in the resulting object, 
 * showing the respective values from both JSON objects.
 * 
 * Both json1 and json2 must have the same structure for the comparison to work correctly.
 * 
 * Object: A JSON object that contains the fields where the values in json1 and json2 differ. The keys in the resulting object correspond to the differing fields, and each key contains an object with two properties:
  json1: The value from json1 (or undefined if the field is missing in json1).
  json2: The value from json2 (or undefined if the field is missing in json2).
  If there are no differences, the function returns an empty object

 * @param {*} json1  old json
 * @param {*} json2  new json
 * @returns 
 */
export function compareJson(json1, json2) {
  // Helper function to compare two objects recursively
  function compareObjects(obj1, obj2) {
    let differences = {};

    // Loop through the keys of the first object
    for (let key in obj1) {
      if (obj1.hasOwnProperty(key)) {
        // If the key exists in both objects
        if (obj2.hasOwnProperty(key)) {
          // If values are objects, recursively compare them
          if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
            const nestedDifferences = compareObjects(obj1[key], obj2[key]);
            if (Object.keys(nestedDifferences).length > 0) {
              differences[key] = nestedDifferences; // If differences found in nested objects, add to result
            }
          } else if (obj1[key] !== obj2[key]) {
            // If values are different, add to the result
            differences[key] = { oldValue: obj1[key], newValue: obj2[key] };
          }
        } else {
          // If key is only in the first object
          differences[key] = { oldValue: obj1[key], newValue: undefined };
        }
      }
    }

    // Check if there are keys in obj2 that don't exist in obj1
    for (let key in obj2) {
      if (obj2.hasOwnProperty(key) && !obj1.hasOwnProperty(key)) {
        differences[key] = { json1: undefined, json2: obj2[key] };
      }
    }

    return differences;
  }

  return compareObjects(json1, json2);
}
